import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { bridge } from '~/services/bridge'

export interface IDEProject {
  name: string
  path: string
}

interface PluginWorkspaceData {
  projects: IDEProject[]
  selectedWorkspace: IDEProject | null
  selectWorkspace: (path: string) => void
  isLoading: boolean
}

const PluginWorkspaceContext = createContext<PluginWorkspaceData | null>(null)

function nameFromPath(p: string): string {
  return p.split(/[\\/]/).filter(Boolean).pop() || 'Workspace'
}

export function PluginWorkspaceProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<IDEProject[]>([])
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const applyProjects = useCallback((resolved: IDEProject[]) => {
    setProjects(resolved)
    setSelectedPath(prev => {
      if (prev && resolved.some(p => p.path === prev)) return prev
      return resolved[0]?.path ?? null
    })
    setIsLoading(false)
  }, [])

  useEffect(() => {
    bridge
      .getWorkspaceInfo()
      .then(result => {
        if (result.success && result.data?.projects?.length) {
          applyProjects(
            result.data.projects.filter((p): p is IDEProject => Boolean(p.name && p.path))
          )
        } else {
          applyProjects([])
        }
      })
      .catch(() => applyProjects([]))

    const handler = (payload: unknown) => {
      const data = payload as { workspacePaths?: string[]; projects?: IDEProject[] } | undefined
      if (Array.isArray(data?.projects) && data.projects.length > 0) {
        applyProjects(data.projects.filter((p): p is IDEProject => Boolean(p.name && p.path)))
      } else {
        const paths = data?.workspacePaths
        if (Array.isArray(paths)) {
          applyProjects(paths.filter(Boolean).map(p => ({ name: nameFromPath(p), path: p })))
        } else {
          applyProjects([])
        }
      }
    }
    bridge.on('workspaceFoldersChanged', handler)
    return () => {
      bridge.off('workspaceFoldersChanged', handler)
    }
  }, [applyProjects])

  const selectWorkspace = useCallback((path: string) => {
    setSelectedPath(path)
  }, [])

  const selectedWorkspace = projects.find(p => p.path === selectedPath) ?? null

  return (
    <PluginWorkspaceContext.Provider
      value={{ projects, selectedWorkspace, selectWorkspace, isLoading }}
    >
      {children}
    </PluginWorkspaceContext.Provider>
  )
}

export function usePluginWorkspace(): PluginWorkspaceData {
  const context = useContext(PluginWorkspaceContext)
  if (!context) {
    throw new Error('usePluginWorkspace must be used within a PluginWorkspaceProvider')
  }
  return context
}

/**
 * Safe version that returns null outside a PluginWorkspaceProvider
 * (e.g. when running in the web app, not the plugin).
 */
export function usePluginWorkspaceSafe(): PluginWorkspaceData | null {
  return useContext(PluginWorkspaceContext)
}
